<form [formGroup]="accountFormGroup" *ngIf="showForm">
    <div class="container my-3">
        <div class="row">
            <div class="col">
                <div class="alert alert-dark">
                    <div class="form-check">
                        <input type="checkbox" class="form-check-input" formControlName="acknowledgement" aria-label="Acknowledgement" id="acknowledgement">
                        <label class="form-check-label" for="acknowledgement">
                            I acknowledge reading and agree to abide by the rules set forth in the University Credit Card Policy before submitting my request for a new merchant account.
                        </label>
                    </div>
                </div>
            </div>
        </div>
        <div class="row" *ngIf="acknowledgement.value == true">
            <div class="col-9">
                <div class="alert alert-dark">
                    <p>Treasury Operations has negotiated a University-wide contract for credit card processing services. The department must complete the following form to establish a new merchant account with the University processor.</p>
                    <p>All merchant accounts are established to accept MasterCard, Visa, Discover, and American Express.</p>
                    <p>The completed form will be submitted into Workflow with the following approvals required:</p>
                    <ol class="bold">
                        <li>Department Administrator</li>
                        <li>Org Budget Officer</li>
                        <li>IT Security Office (dependent on processing method)</li>
                        <li>Controller's Office</li>
                        <li>Treasury Operations</li>
                    </ol>
                    <p>Please contact <a href="mailto:treasury-creditcards@uiowa.edu">treasury-creditcards@uiowa.edu</a> with any questions on the new merchant account request form.</p>
                </div>
                <div *ngIf="!isStatusDraft()">
                    <app-workflow [packageId]="merchantAccount.workflowPackageId"></app-workflow>
                </div>
                <app-general-information formGroupName="generalInformation" [showErrors]="showErrors" (onMerchantProfileChange)="onMerchantProfileChange($event)"></app-general-information>
                <app-primary-contact-information formGroupName="primaryContactInformation" [showErrors]="showErrors"></app-primary-contact-information>
                <app-mfk-information formGroupName="mfkInformation" [showErrors]="showErrors"></app-mfk-information>
                <app-sales-information formGroupName="salesInformation" [showErrors]="showErrors" (onCardAcceptanceChannelChange)="onCardAcceptanceChannelChange($event)" *ngIf="!isCashnet"></app-sales-information>
                <app-processing-information formGroupName="processingInformation" [showErrors]="showErrors"></app-processing-information>
                <h2 class="page-header" id="additional">Additional Information <small class="text-warning">Optional</small></h2>
                <hr/>
                <div class="mb-3 row">
                    <label for="additionalInformation" class="col-5 col-form-label text-end">Additional Information</label>
                    <div class="col">
                        <textarea class="form-control" id="additionalInformation" formControlName="additionalInformation" maxlength="2500"></textarea>
                    </div>
                </div>
            </div>
            <div class="col-3">
                <div class="sticky-top">
                    <p class="mb-0"><b>Account Status</b> <span [className]="accountStatusClass">{{merchantAccount.statusString}}</span></p>
                    <div class="list-group mb-3">
                        <div class="list-group-item d-flex justify-content-between align-items-center">
                            General Information
                            <span class="badge text-bg-success" *ngIf="generalInformation.valid">
                                <fa-icon [icon]="['fas', 'check-square']"></fa-icon>
                            </span>
                            <span class="badge text-bg-danger" *ngIf="generalInformation.invalid">
                                <fa-icon [icon]="['fas', 'exclamation']"></fa-icon>
                            </span>
                        </div>
                        <div class="list-group-item d-flex justify-content-between align-items-center">
                            Primary Contact Information
                            <span class="badge text-bg-success" *ngIf="primaryContactInformation.valid">
                                <fa-icon [icon]="['fas', 'check-square']"></fa-icon>
                            </span>
                            <span class="badge text-bg-danger" *ngIf="primaryContactInformation.invalid">
                                <fa-icon [icon]="['fas', 'exclamation']"></fa-icon>
                            </span>
                        </div>
                        <div class="list-group-item d-flex justify-content-between align-items-center">
                            MFK Information
                            <span class="badge text-bg-success" *ngIf="mfkInformation.valid">
                                <fa-icon [icon]="['fas', 'check-square']"></fa-icon>
                            </span>
                            <span class="badge text-bg-danger" *ngIf="mfkInformation.invalid">
                                <fa-icon [icon]="['fas', 'exclamation']"></fa-icon>
                            </span>
                        </div>
                        <div class="list-group-item d-flex justify-content-between align-items-center" *ngIf="!isCashnet">
                            Sales Information
                            <span class="badge text-bg-success" *ngIf="salesInformation.valid">
                                <fa-icon [icon]="['fas', 'check-square']"></fa-icon>
                            </span>
                            <span class="badge text-bg-danger" *ngIf="salesInformation.invalid">
                                <fa-icon [icon]="['fas', 'exclamation']"></fa-icon>
                            </span>
                        </div>
                        <div class="list-group-item d-flex justify-content-between align-items-center">
                            Processing Information
                            <span class="badge text-bg-success" *ngIf="processingInformation.valid || processingInformation.disabled">
                                <fa-icon [icon]="['fas', 'check-square']"></fa-icon>
                            </span>
                            <span class="badge text-bg-danger" *ngIf="processingInformation.invalid">
                                <fa-icon [icon]="['fas', 'exclamation']"></fa-icon>
                            </span>
                        </div>
                    </div>
                    <div class="d-grid gap-2">
                        <button type="button" class="btn btn-lg btn-save btn-uiowa-dark" *ngIf="isStatusDraft()" (click)="saveAsDraft()" role="button" aria-label="Save as Draft" [disabled]="saving">
                            <fa-icon [icon]="['fas', 'save']"></fa-icon> Save as Draft
                        </button>
                        <button type="button" class="btn btn-lg btn-block btn-save btn-uiowa-dark" *ngIf="isStatusDraft()" (click)="saveAndSubmit()" role="button" aria-label="Save and Submit to Workflow" [disabled]="saving">
                            <fa-icon [icon]="['fas', 'paper-plane']"></fa-icon> Save and Submit to Workflow
                        </button>
                        <button type="button" class="btn btn-lg btn-block btn-save btn-uiowa-dark" *ngIf="isStatusRouting()" (click)="saveAndRestartWorkflow()" role="button" aria-label="Save and Restart Workflow" [disabled]="saving">
                            <fa-icon [icon]="['fas', 'redo']"></fa-icon> Save and Restart Workflow
                        </button>
                        <button type="button" class="btn btn-lg btn-block btn-save btn-uiowa-dark" *ngIf="isStatusCompleteOrFinalized()" (click)="save()" role="button" aria-label="Save" [disabled]="saving">
                            <fa-icon [icon]="['fas', 'save']"></fa-icon> Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</form>